import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navigation from "../components/Navigation";
import {
  getBillingConfig,
  createCheckoutSession,
  BillingProduct,
  CreditPack,
} from "../api/billing";
import { getAccessToken } from "../utils/auth";

const PricingPage: React.FC = () => {
  const navigate = useNavigate();
  const [billingInterval, setBillingInterval] = useState<"monthly" | "annual">("monthly");
  const [products, setProducts] = useState<BillingProduct[]>([]);
  const [creditPacks, setCreditPacks] = useState<CreditPack[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    const token = getAccessToken();
    setIsLoggedIn(!!token);

    getBillingConfig()
      .then((config) => {
        setProducts(config.products);
        setCreditPacks(config.credit_packs);
        setLoading(false);
      })
      .catch((err) => {
        setError("Unable to load pricing information. Please try again later.");
        setLoading(false);
        console.error("Failed to load billing config:", err);
      });
  }, []);

  const handleSubscribe = async (priceId: string) => {
    if (!isLoggedIn) {
      navigate("/login");
      return;
    }

    setCheckoutLoading(priceId);
    try {
      const url = await createCheckoutSession(priceId, "subscription");
      window.location.href = url;
    } catch (err) {
      setError("Failed to start checkout. Please try again.");
      console.error("Checkout error:", err);
    } finally {
      setCheckoutLoading(null);
    }
  };

  const handleBuyCredits = async (priceId: string) => {
    if (!isLoggedIn) {
      navigate("/login");
      return;
    }

    setCheckoutLoading(priceId);
    try {
      const url = await createCheckoutSession(priceId, "payment");
      window.location.href = url;
    } catch (err) {
      setError("Failed to start checkout. Please try again.");
      console.error("Checkout error:", err);
    } finally {
      setCheckoutLoading(null);
    }
  };

  const imageProducts = products.filter((p) => p.product_type === "image_tier");
  const claimsProducts = products.filter((p) => p.product_type === "claims_tier");

  const isMonthly = billingInterval === "monthly";
  const isAnnual = billingInterval === "annual";

  if (loading) {
    return (
      <>
        <Navigation />
        <div className="min-h-screen bg-[#0a0b0f] flex items-center justify-center">
          <div className="text-zinc-400">Loading pricing...</div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-[#0a0b0f] bg-grid-pattern relative overflow-hidden">
        <div className="absolute inset-0 bg-radial-gradient pointer-events-none" />

        <div className="relative max-w-6xl mx-auto px-4 py-12">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-zinc-100 mb-3">
              Choose Your Plan
            </h1>
            <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
              Professional photo management and claims tools for insurance adjusters
            </p>
          </div>

          {error && (
            <div className="alert alert-error mb-8 max-w-xl mx-auto text-center">
              {error}
            </div>
          )}

          {/* Billing Interval Toggle */}
          <div className="flex items-center justify-center gap-3 mb-10">
            <span className={"text-sm font-medium " + (isMonthly ? "text-amber-400" : "text-zinc-500")}>
              Monthly
            </span>
            <button
              onClick={() => setBillingInterval(isMonthly ? "annual" : "monthly")}
              className={"relative w-14 h-7 rounded-full transition-colors " + (isAnnual ? "bg-amber-500" : "bg-zinc-700")}
            >
              <span
                className={"absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white transition-transform " + (isAnnual ? "translate-x-7" : "")}
              />
            </button>
            <span className={"text-sm font-medium " + (isAnnual ? "text-amber-400" : "text-zinc-500")}>
              Annual
            </span>
            {isAnnual && (
              <span className="badge badge-success text-xs ml-2">Save ~17%</span>
            )}
          </div>

          {/* Image Service Tiers */}
          <div className="mb-16">
            <h2 className="text-2xl font-semibold text-zinc-100 text-center mb-2">
              Photo Management
            </h2>
            <p className="text-zinc-500 text-center mb-8">
              Secure photo hosting with geolocation tagging
            </p>

            <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {/* Free tier card */}
              <div className="card-dark rounded-2xl p-6 flex flex-col">
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-zinc-100">Free</h3>
                  <div className="mt-2">
                    <span className="text-3xl font-bold text-zinc-100">$0</span>
                    <span className="text-zinc-500">/mo</span>
                  </div>
                </div>
                <ul className="space-y-3 mb-8 flex-1">
                  {["3 active locations", "1 download per location", "7-day location expiry"].map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-zinc-400">
                      <svg className="w-4 h-4 text-zinc-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {f}
                    </li>
                  ))}
                </ul>
                <button
                  disabled
                  className="w-full py-2.5 rounded-xl text-sm font-medium bg-zinc-800 text-zinc-500 cursor-not-allowed"
                >
                  Current Plan
                </button>
              </div>

              {/* Paid tier cards */}
              {imageProducts.map((product) => {
                const priceId = isMonthly ? product.monthly_price_id : product.annual_price_id;
                const price = isMonthly ? product.monthly_price : product.annual_price;
                const isPopular = product.tier_id === "premium";
                return (
                  <div
                    key={product.tier_id}
                    className={"card-dark rounded-2xl p-6 flex flex-col relative " + (isPopular ? "ring-2 ring-amber-500/50" : "")}
                  >
                    {isPopular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <span className="badge badge-amber text-xs px-3 py-1">Most Popular</span>
                      </div>
                    )}
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-zinc-100">{product.display_name}</h3>
                      <div className="mt-2">
                        <span className="text-3xl font-bold text-zinc-100">{price}</span>
                        <span className="text-zinc-500">/{isMonthly ? "mo" : "yr"}</span>
                      </div>
                    </div>
                    <ul className="space-y-3 mb-8 flex-1">
                      {product.features.map((f) => (
                        <li key={f} className="flex items-start gap-2 text-sm text-zinc-400">
                          <svg className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          {f}
                        </li>
                      ))}
                    </ul>
                    <button
                      onClick={() => handleSubscribe(priceId)}
                      disabled={checkoutLoading === priceId}
                      className={"w-full py-2.5 rounded-xl text-sm font-semibold transition-all " +
                        (isPopular ? "btn-primary" : "btn-secondary hover:border-amber-500/50") +
                        (checkoutLoading === priceId ? " opacity-50 cursor-wait" : "")}
                    >
                      {checkoutLoading === priceId ? "Loading..." : "Subscribe"}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Claims Service */}
          {claimsProducts.length > 0 && (
            <div className="mb-16">
              <h2 className="text-2xl font-semibold text-zinc-100 text-center mb-2">
                Claims Management
              </h2>
              <p className="text-zinc-500 text-center mb-8">
                Multi-device sync and AI-powered claim tools
              </p>

              <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto">
                {/* Free claims tier */}
                <div className="card-dark rounded-2xl p-6 flex flex-col">
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-zinc-100">Claims Free</h3>
                    <div className="mt-2">
                      <span className="text-3xl font-bold text-zinc-100">$0</span>
                      <span className="text-zinc-500">/mo</span>
                    </div>
                  </div>
                  <ul className="space-y-3 mb-8 flex-1">
                    {["Multi-device sync", "Basic claim management"].map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-zinc-400">
                        <svg className="w-4 h-4 text-zinc-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        {f}
                      </li>
                    ))}
                  </ul>
                  <button disabled className="w-full py-2.5 rounded-xl text-sm font-medium bg-zinc-800 text-zinc-500 cursor-not-allowed">
                    Free with Account
                  </button>
                </div>

                {/* Paid claims tier */}
                {claimsProducts.map((product) => {
                  const priceId = isMonthly ? product.monthly_price_id : product.annual_price_id;
                  const price = isMonthly ? product.monthly_price : product.annual_price;
                  return (
                    <div key={product.tier_id} className="card-dark rounded-2xl p-6 flex flex-col ring-2 ring-amber-500/50">
                      <div className="mb-6">
                        <h3 className="text-lg font-semibold text-zinc-100">{product.display_name}</h3>
                        <div className="mt-2">
                          <span className="text-3xl font-bold text-zinc-100">{price}</span>
                          <span className="text-zinc-500">/{isMonthly ? "mo" : "yr"}</span>
                        </div>
                      </div>
                      <ul className="space-y-3 mb-8 flex-1">
                        {product.features.map((f) => (
                          <li key={f} className="flex items-start gap-2 text-sm text-zinc-400">
                            <svg className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            {f}
                          </li>
                        ))}
                      </ul>
                      <button
                        onClick={() => handleSubscribe(priceId)}
                        disabled={checkoutLoading === priceId}
                        className={"w-full py-2.5 rounded-xl text-sm font-semibold btn-primary" +
                          (checkoutLoading === priceId ? " opacity-50 cursor-wait" : "")}
                      >
                        {checkoutLoading === priceId ? "Loading..." : "Subscribe"}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* AI Credits */}
          {creditPacks.length > 0 && (
            <div className="mb-16">
              <h2 className="text-2xl font-semibold text-zinc-100 text-center mb-2">
                AI Credits
              </h2>
              <p className="text-zinc-500 text-center mb-8">
                One-time credit packs for AI-powered features (ChatGPT, Claude, Deepgram)
              </p>

              <div className="grid md:grid-cols-3 gap-6 max-w-3xl mx-auto">
                {creditPacks.map((pack) => (
                  <div key={pack.price_id} className="card-dark rounded-2xl p-6 flex flex-col text-center">
                    <div className="mb-4">
                      <div className="text-4xl font-bold text-amber-400">{pack.credits}</div>
                      <div className="text-sm text-zinc-500 mt-1">credits</div>
                    </div>
                    <div className="text-2xl font-bold text-zinc-100 mb-1">{pack.price}</div>
                    {pack.savings ? (
                      <div className="text-sm text-emerald-400 mb-4">{pack.savings}</div>
                    ) : (
                      <div className="mb-4" />
                    )}
                    <button
                      onClick={() => handleBuyCredits(pack.price_id)}
                      disabled={checkoutLoading === pack.price_id}
                      className={"w-full py-2.5 rounded-xl text-sm font-semibold btn-secondary hover:border-amber-500/50" +
                        (checkoutLoading === pack.price_id ? " opacity-50 cursor-wait" : "")}
                    >
                      {checkoutLoading === pack.price_id ? "Loading..." : "Buy Credits"}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="text-center text-zinc-600 text-sm">
            <p>All prices in USD. Subscriptions can be canceled anytime.</p>
            <p className="mt-1">
              Questions? Contact{" "}
              <a href="mailto:gavin.pase.develops@gmail.com" className="text-amber-400 hover:underline">
                gavin.pase.develops@gmail.com
              </a>
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default PricingPage;
